import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TreeItem } from "@components_v2/tree-view";
import { ImageDriveOn, ImageFolderSmallOn } from "@mzc-cloudplex/icons";
import FolderTreeViewSkeleton from "./folder-tree-view-skeleton";

// 드래그 호버 시 접힌 폴더를 자동으로 펼치는 지연시간 (ms)
const DRAG_HOVER_EXPAND_DELAY = 300;

const FolderTreeItem = ({
    folder,
    menuList,
    creatingParentFolder,
    foldDisabled,
    onClickFolder,
    onClickCreateFolder,
    onToggleFolderFold,
    onRefreshDrive,
    renderTree,
    onDropTreeItem,
    selectedFolderId,
    canEdit = true,
}) => {
    const onHandleClickRefresh = useCallback(() => {
        onRefreshDrive(folder?.driveId, folder);
    }, [onRefreshDrive, folder]);

    const nodeId = useMemo(() => (folder?.id ? String(folder.id) : ""), [folder?.id]);

    const [isDragOver, setIsDragOver] = useState(false);
    const dragHoverExpandTimerRef = useRef(null);

    const isDroppable = !!onDropTreeItem && !!folder?.id && canEdit;

    const cancelDragHoverExpand = useCallback(() => {
        if (dragHoverExpandTimerRef.current) {
            clearTimeout(dragHoverExpandTimerRef.current);
            dragHoverExpandTimerRef.current = null;
        }
    }, []);

    const scheduleDragHoverExpand = useCallback(() => {
        if (foldDisabled) return;
        if (!folder?.isFold) return;
        // 비어있다고 확인된 폴더는 건너뜀. 아직 fetch 안 한 폴더는 펼침 시 API 호출됨.
        if (folder?.isEmpty === true) return;
        if (dragHoverExpandTimerRef.current) return;
        dragHoverExpandTimerRef.current = setTimeout(() => {
            dragHoverExpandTimerRef.current = null;
            onToggleFolderFold(folder);
        }, DRAG_HOVER_EXPAND_DELAY);
    }, [foldDisabled, folder, onToggleFolderFold]);

    useEffect(() => cancelDragHoverExpand, [cancelDragHoverExpand]);

    const handleDragOver = useCallback(
        (e) => {
            if (!isDroppable) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "move";
            if (!isDragOver) {
                setIsDragOver(true);
                scheduleDragHoverExpand();
            }
        },
        [isDroppable, isDragOver, scheduleDragHoverExpand],
    );

    const handleDragLeave = useCallback(
        (e) => {
            if (!isDroppable) return;
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            cancelDragHoverExpand();
        },
        [isDroppable, cancelDragHoverExpand],
    );

    const handleDrop = useCallback(
        (e) => {
            if (!isDroppable) return;
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            cancelDragHoverExpand();
            if (folder.id === selectedFolderId) return;
            onDropTreeItem(folder);
        },
        [isDroppable, folder, selectedFolderId, onDropTreeItem, cancelDragHoverExpand],
    );

    if (!folder?.id) return null;

    return (
        <TreeItem
            key={folder.id}
            type={"Folder"}
            depth={folder.depth}
            icon={
                folder.depth === 0 ? (
                    <ImageDriveOn height={16} width={16} />
                ) : (
                    <ImageFolderSmallOn height={16} width={16} />
                )
            }
            foldDisabled={foldDisabled}
            nodeId={nodeId}
            label={`${folder?.name}`}
            isOverCurrent={isDragOver}
            onDragOver={isDroppable ? handleDragOver : undefined}
            onDragLeave={isDroppable ? handleDragLeave : undefined}
            onDrop={isDroppable ? handleDrop : undefined}
            onClick={(e) => {
                e.stopPropagation();
                onClickFolder(folder, false);
            }}
            onClickExpand={() => {
                // 설명: 열기/닫기 아이콘 전용
                onToggleFolderFold(folder);
            }}
            onClickRefresh={folder.type === "drive" && onRefreshDrive ? onHandleClickRefresh : undefined}
            menuList={menuList}
            sx={{ ".MuiTypography-root": { fontSize: "14px", fontWeight: "normal" } }}
        >
            {creatingParentFolder?.id === folder.id && (
                <TreeItem
                    key={`inner_${folder.id}`}
                    depth={folder.depth + 1}
                    icon={<ImageFolderSmallOn height={16} width={16} />}
                    nodeId={"create"}
                    onSubmitField={(folderName) => {
                        onClickCreateFolder(folderName, folder.id);
                    }}
                ></TreeItem>
            )}
            {folder.children?.length > 0 && renderTree(folder?.id, folder?.children)}
            {folder.pending && <FolderTreeViewSkeleton type={"child"} depth={folder.depth + 1} />}
        </TreeItem>
    );
};

export default FolderTreeItem;
